import React, { useContext, useEffect, useRef, useState } from "react"
import AuthenticationContext from "../context/authentication/AuthenticationContext"
import NotificationContext from "../context/Notification/NotificationContext"
import { stopImpersonation, impersonateUser, sessToken, searchUserByEmail } from "../helpers/DrupalHelper"
import StateContext from "../context/state/StateContext"

// Admin user IDs allowed to impersonate other users.
// Reads from .env (GATSBY_ADMIN_USER_IDS) or falls back to hardcoded array.
const ADMIN_IMPERSONATION_USER_IDS = process.env.GATSBY_ADMIN_USER_IDS
  ? process.env.GATSBY_ADMIN_USER_IDS.split(',').map(id => parseInt(id.trim(), 10))
  : [1, 2046]

const ImpersonationBanner = () => {
  const { authentication, runLogin } = useContext(AuthenticationContext)
  const { toggleNotification } = useContext(NotificationContext)
  const { state, dispatch } = useContext(StateContext)
  const [busy, setBusy] = useState(false)
  const [targetUserId, setTargetUserId] = useState('')
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [position, setPosition] = useState({ x: 16, y: 200 })
  const bannerRef = useRef(null)
  const dragState = useRef({ active: false, offsetX: 0, offsetY: 0 })

  const currentUserId = parseInt(authentication?.currentUserData?.i, 10)
  const originalUserId = authentication?.currentUserData?.original_uid ? parseInt(authentication?.currentUserData?.original_uid, 10) : null

  // Only truly impersonating if isImpersonating flag is set AND current user differs from original user.
  const isImpersonating = authentication?.currentUserData?.isImpersonating &&
                          originalUserId &&
                          originalUserId !== currentUserId

  const impersonatedName = authentication?.currentUserData?.firstname + ' ' + authentication?.currentUserData?.lastname
  const impersonatedEmail = authentication?.currentUserData?.email
  const isAdmin = ADMIN_IMPERSONATION_USER_IDS.includes(currentUserId)

  // All useEffect hooks must be called before any conditional returns.
  useEffect(() => {
    if (typeof window === 'undefined') return
    // Drop the banner near the lower-left by default once mounted.
    setPosition(prev => ({
      x: prev.x,
      y: Math.min(window.innerHeight * 0.8, window.innerHeight - 120)
    }))
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleMove = (event) => {
      if (!dragState.current.active) return
      const point = event.touches?.[0] || event

      const bannerWidth = bannerRef.current?.offsetWidth || 0
      const bannerHeight = bannerRef.current?.offsetHeight || 0
      const maxX = Math.max(window.innerWidth - bannerWidth - 8, 8)
      const maxY = Math.max(window.innerHeight - bannerHeight - 8, 8)

      const nextX = Math.min(Math.max(point.clientX - dragState.current.offsetX, 8), maxX)
      const nextY = Math.min(Math.max(point.clientY - dragState.current.offsetY, 8), maxY)

      setPosition({ x: nextX, y: nextY })
      if (event.cancelable) event.preventDefault()
    }

    const stopDrag = () => {
      dragState.current.active = false
    }

    window.addEventListener('mousemove', handleMove)
    window.addEventListener('touchmove', handleMove, { passive: false })
    window.addEventListener('mouseup', stopDrag)
    window.addEventListener('touchend', stopDrag)

    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('touchmove', handleMove)
      window.removeEventListener('mouseup', stopDrag)
      window.removeEventListener('touchend', stopDrag)
    }
  }, [])

  // Show nothing if not impersonating and not admin.
  if (!isImpersonating && !isAdmin) {
    return null
  }

  const handleExit = async () => {
    try {
      setBusy(true)
      const token = state.token || (await sessToken())
      if (!state.token) dispatch({ type: "SET_TOKEN", payload: token })

      const res = await stopImpersonation(token)
      if (res && res.success) {
        const newToken = await sessToken()
        dispatch({ type: "SET_TOKEN", payload: newToken })
        await runLogin()
        toggleNotification({ content: "You are now logged in as yourself again." })
      } else {
        toggleNotification({ content: `Failed to stop impersonation`, error: true })
      }
    } catch (e) {
      toggleNotification({ content: `Failed to stop impersonation`, error: true })
    } finally {
      setBusy(false)
    }
  }

  const handleImpersonate = async () => {
    if (!targetUserId || targetUserId.trim() === '') {
      toggleNotification({ content: 'Please enter a user ID or email', error: true })
      return
    }

    try {
      setBusy(true)
      const token = state.token || await sessToken()
      if (!state.token) dispatch({ type: "SET_TOKEN", payload: token })

      let userIdToImpersonate = targetUserId
      let displayName = targetUserId

      // Check if input is an email (contains @)
      if (targetUserId.includes('@')) {
        const userData = await searchUserByEmail(token, targetUserId)
        if (!userData || !userData.uid) {
          toggleNotification({
            content: `User not found with email: ${targetUserId}`,
            error: true
          })
          setBusy(false)
          return
        }
        userIdToImpersonate = userData.uid
        displayName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.email
      }

      const res = await impersonateUser(token, userIdToImpersonate)

      if (res && res.success) {
        const newToken = await sessToken()
        dispatch({ type: "SET_TOKEN", payload: newToken })
        await runLogin()
        toggleNotification({ content: `Now impersonating ${displayName}` })
        setTargetUserId('') // Clear input
      } else {
        toggleNotification({
          content: `Failed to impersonate user: ${res?.message || 'Permission denied'}`,
          error: true
        })
      }
    } catch (e) {
      console.error('Impersonation error:', e)
      toggleNotification({
        content: `Error: ${e.message || 'Failed to impersonate user'}`,
        error: true
      })
    } finally {
      setBusy(false)
    }
  }

  const startDrag = (event) => {
    if (!bannerRef.current) return
    const point = event.touches?.[0] || event
    const rect = bannerRef.current.getBoundingClientRect()
    dragState.current = {
      active: true,
      offsetX: point.clientX - rect.left,
      offsetY: point.clientY - rect.top
    }
    event.preventDefault()
  }

  const headerIcon = isImpersonating ? '⚠️' : '🔑'
  const headerText = isImpersonating ? `Impersonating ${impersonatedName || ''}`.trim() : 'Admin: Login as User'

  const impersonatingContent = (
    <>
      <div className="flex items-start gap-3 mb-3">
        <span className="text-2xl">⚠️</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm mb-1 text-white">Impersonating:</p>
          <p className="font-bold text-base truncate text-white">{impersonatedName}</p>
          <p className="text-xs truncate" style={{ color: '#ccc' }}>{impersonatedEmail}</p>
        </div>
      </div>
      <button
        onClick={handleExit}
        disabled={busy}
        className="w-full bg-white font-semibold px-4 py-2 rounded hover:bg-gray-100 transition disabled:opacity-50"
        style={{ color: '#333' }}
      >
        {busy ? 'Exiting…' : 'Exit Impersonation'}
      </button>
    </>
  )

  const adminContent = (
    <>
      <input
        type="text"
        value={targetUserId}
        onChange={(e) => setTargetUserId(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter') handleImpersonate()
        }}
        placeholder="Enter User ID or Email"
        className="w-full border border-gray-300 rounded px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        disabled={busy}
      />
      <button
        onClick={handleImpersonate}
        disabled={busy}
        className="w-full bg-white font-semibold px-4 py-2 rounded hover:bg-gray-100 transition disabled:opacity-50"
        style={{ color: '#333' }}
      >
        {busy ? 'Logging in...' : 'Login as User'}
      </button>
    </>
  )

  const content = isImpersonating ? impersonatingContent : adminContent

  return (
    <div
      ref={bannerRef}
      className="fixed z-[9999] w-[320px] max-w-[90vw]"
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
    >
      <div className="rounded-lg shadow-2xl" style={{ backgroundColor: '#333' }}>
        <div
          className="flex items-center justify-between px-3 py-2 cursor-move select-none"
          onMouseDown={startDrag}
          onTouchStart={startDrag}
        >
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="text-xl shrink-0">{headerIcon}</span>
            <div className="text-white text-sm font-semibold truncate">
              {headerText || (isImpersonating ? 'Impersonating' : 'Impersonation Tools')}
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setIsCollapsed(prev => !prev)
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="text-white/80 hover:text-white p-1 rounded"
            aria-label={isCollapsed ? 'Expand banner' : 'Collapse banner'}
          >
            <span className={`inline-block transition-transform ${isCollapsed ? 'rotate-180' : ''}`}>↓</span>
          </button>
        </div>
        {!isCollapsed && (
          <div className="p-4 pt-3 border-t border-white/10">
            {content}
          </div>
        )}
      </div>
    </div>
  )
}

export default ImpersonationBanner
